export const MAX_FILE_SIZE_BYTES = 1_000_000_000; // 1gb in bytes

export enum LinkType {
  MUX_VIDEO = "MUX_VIDEO",
  VIDEO = "VIDEO",
  HOSTED_VIDEO = "HOSTED_VIDEO",
  IMAGE = "IMAGE",
  GIF = "GIF",
  AUDIO = "AUDIO",
  LINK = "LINK",
  VCARD = "VCARD",
  PDF = "PDF",
  EMOJI = "EMOJI",
  WEBSITE = "WEBSITE",
}

export const linkTypeCopy: { [key in LinkType]: string } = {
  [LinkType.MUX_VIDEO]: "Video",
  [LinkType.VIDEO]: "Video",
  [LinkType.HOSTED_VIDEO]: "Video",
  [LinkType.IMAGE]: "Image",
  [LinkType.GIF]: "Gif",
  [LinkType.AUDIO]: "Audio",
  [LinkType.LINK]: "Link",
  [LinkType.VCARD]: "Contact Card",
  [LinkType.PDF]: "PDF",
  [LinkType.EMOJI]: "Emoji",
  [LinkType.WEBSITE]: "Website",
};

export function getLinkType(link: string): LinkType {
  const extension = getExtension(link);
  if (!extension) {
    return LinkType.LINK;
  } else if (GIF_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.GIF;
  } else if (IMAGE_EXTENSIONS.indexOf(extension) !== -1 || isUnsplash(link)) {
    return LinkType.IMAGE;
  } else if (AUDIO_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.AUDIO;
  } else if (isMux(link)) {
    return LinkType.MUX_VIDEO;
  } else if (VIDEO_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.VIDEO;
  } else if (VCARD_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.VCARD;
  } else if (isYoutube(link) || isVimeo(link)) {
    return LinkType.HOSTED_VIDEO;
  } else if (PDF_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.PDF;
  } else if (WEBSITE_EXTENSIONS.indexOf(extension) !== -1) {
    return LinkType.WEBSITE;
  } else {
    return LinkType.LINK;
  }
}

/**
 * A link to a web page rather than a file. `getLinkType` calls anything it
 * doesn't recognise a link — an uploaded Word, PowerPoint or spreadsheet file
 * included — so a URL ending in one of the upload types is a file, not a link.
 */
export function isWebLink(link: string): boolean {
  if (getLinkType(link) !== LinkType.LINK) return false;
  const extension = getExtension(link);
  return !extension || !ACCEPTED_FILE_TYPES.includes(`.${extension}`);
}

export function isVideoOrMuxVideo(linkType: LinkType): boolean {
  return [LinkType.MUX_VIDEO, LinkType.VIDEO].includes(linkType);
}

function isYoutube(link: string): boolean {
  return link.toLowerCase().includes("youtube.com");
}

function isVimeo(link: string): boolean {
  return link.toLowerCase().includes("vimeo.com");
}

export function isMux(link: string): boolean {
  return link.toLowerCase().includes("stream.mux.com");
}

function isUnsplash(link: string): boolean {
  return link.toLowerCase().includes("images.unsplash.com");
}

export function isHls(link: string): boolean {
  const ext = getExtension(link);
  return ext ? HLS_EXTENSIONS.indexOf(ext) > -1 : false;
}

const WEBSITE_EXTENSIONS = ["html"];
const HLS_EXTENSIONS = ["m3u8"];
const GIF_EXTENSIONS = ["gif"];
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp"];
const AUDIO_EXTENSIONS = ["mp3"];
const VCARD_EXTENSIONS = ["vcf"];
const PDF_EXTENSIONS = ["pdf"];
const VIDEO_EXTENSIONS = ["avi", "m3u8", "mov", "mp4", "mpeg", "mpg", "webm"];

export const ACCEPTED_FILE_TYPES = [
  ".gif",
  ".png",
  ".jpg",
  ".jpeg",
  ".mp3",
  ".m4a",
  ".wav",
  ".pdf",
  ".mov",
  ".mp4",
  ".webm",
  ".pptm",
  ".pptx",
  ".webp",
  ".doc",
  ".docx",
  ".csv",
  ".xls",
  ".xlsx",
];
export const ACCEPTED_FILE_TYPES_GROUPED = {
  "image/*": [".gif", ".png", ".jpg", ".jpeg", ".webp"],
  "audio/*": [".mp3", ".m4a", ".wav"],
  "application/pdf": [".pdf"],
  "video/*": [".mov", ".mp4", ".webm"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
    ".pptm",
  ],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "text/csv": [".csv"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
};
export const ACCEPTED_IMAGE_TYPES = [".png", ".jpg", ".jpeg"];
export function getExtension(link: string): string | undefined {
  return link.split(".").pop()?.split("?")[0]?.toLowerCase();
}

export function getMuxVideoId(link: string): string {
  const regExp = /^.*(stream.mux.com\/)([^#&?]*).m3u8/;
  const match = link.match(regExp);
  return match?.[2] || "";
}

export function humanFileSize(size: number): string {
  const i = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return (
    (size / Math.pow(1024, i)).toFixed(1) +
    " " +
    ["B", "kB", "MB", "GB", "TB"][i]
  );
}

export const validateFileType = (file: File): boolean => {
  const fileExtension = file.name
    .substring(file.name.lastIndexOf("."))
    .toLowerCase();
  return ACCEPTED_FILE_TYPES.includes(fileExtension);
};
